// Auth0's My Account API, which adds a passkey or a recovery code for the signed-in customer and
// removes an old recovery code that customers-api cannot see. Its token comes from an authorization
// code flow with PKCE, run here rather than through oidc-client, whose callback would keep the code
// for the dashboard's own session. The same round trip also signs a passkey-only customer in again
// before customers-api removes a passkey.

import { myAccountCallback } from '#client/constants/routes';

// The step-up value the Enforce MFA Policy Action already honours; the /me/ audience alone also
// forces a fresh challenge there.
const STEP_UP_ACR_VALUES = 'http://schemas.openid.net/pape/policies/2007/06/multi-factor';
// Only the verifier, state and operation survive the redirect; the token never leaves memory.
const PENDING_KEY = 'myAccount.pending';
// The Enforce MFA Policy Action's error_description when it refuses a /me/ token to a customer with
// no passkey sign-in in the last five minutes and no two-step verification method to challenge.
export const MY_ACCOUNT_NEEDS_TWO_STEP = 'two-step verification is required to change sign-in methods';

export const MY_ACCOUNT_OPERATIONS = {
  passkey: 'passkey',
  recoveryCode: 'recovery-code',
  // Not a My Account API call: only the fresh sign-in matters, which Auth0 records on the passkey's
  // last_auth_at for customers-api to read. No acr_values, which would make the Action enroll SMS.
  signIn: 'sign-in',
};

// Each token asks only for what its operation does. Create also covers verify. Read and delete let a
// new recovery code replace an older one that only the My Account API lists: Auth0 accepts only the
// oldest at sign-in, and one left unconfirmed by an abandoned Universal Login enrollment is missing
// from the Management API customers-api reads.
const SCOPES = {
  [MY_ACCOUNT_OPERATIONS.passkey]: 'create:me:authentication_methods',
  [MY_ACCOUNT_OPERATIONS.recoveryCode]:
    'create:me:authentication_methods read:me:authentication_methods delete:me:authentication_methods',
};

const bytesToBase64Url = (buffer) =>
  btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

export const base64UrlToBytes = (value) => {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(base64 + '='.repeat((4 - (base64.length % 4)) % 4));
  return Uint8Array.from(binary, (char) => char.charCodeAt(0));
};

const randomBase64Url = (byteLength) => bytesToBase64Url(crypto.getRandomValues(new Uint8Array(byteLength)));

const s256 = async (verifier) =>
  bytesToBase64Url(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier)));

// The audience must name the host the API is called on, so both come from the custom domain.
const myAccountBase = (domain) => `https://${domain}/me/`;

/**
 * Leaves for Auth0 to get a My Account API token for one operation.
 *
 * @param {object} options
 * @param {string} options.domain - The Auth0 custom domain, e.g. login.zenbusiness.com.
 * @param {string} options.clientId - The OIDC client, which is public (no client secret).
 * @param {string} options.operation - One of MY_ACCOUNT_OPERATIONS.
 * @param {string} options.returnTo - Same-origin path to come back to.
 * @param {string[]} [options.replaceMethodIds] - The recovery codes customers-api lists, which a new one replaces.
 * @returns {Promise<void>}
 */
export async function startMyAccountOperation({ domain, clientId, operation, returnTo, replaceMethodIds }) {
  const verifier = randomBase64Url(32);
  const state = randomBase64Url(16);
  sessionStorage.setItem(PENDING_KEY, JSON.stringify({ verifier, state, operation, returnTo, replaceMethodIds }));

  const params = new URLSearchParams({
    client_id: clientId,
    response_type: 'code',
    redirect_uri: `${window.location.origin}${myAccountCallback}`,
    ...(operation === MY_ACCOUNT_OPERATIONS.signIn
      ? { scope: 'openid' }
      : { audience: myAccountBase(domain), scope: SCOPES[operation], acr_values: STEP_UP_ACR_VALUES }),
    // The Action accepts only a passkey sign-in from the last five minutes, so an existing Auth0
    // session must not stand in for one.
    max_age: '0',
    state,
    code_challenge: await s256(verifier),
    code_challenge_method: 'S256',
  });
  window.location.assign(`https://${domain}/authorize?${params}`);
}

/**
 * Reads the operation started before the redirect.
 *
 * @returns {{ verifier: string, state: string, operation: string, returnTo: string, replaceMethodIds?: string[] } | null}
 */
export function readPendingOperation() {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    // Blocked storage or a damaged value means no request to finish; the callback reports that.
    return null;
  }
}

// Forgotten once the callback starts, so a reload cannot replay the operation.
export function clearPendingOperation() {
  try {
    sessionStorage.removeItem(PENDING_KEY);
  } catch {
    // Blocked storage held nothing to forget.
  }
}

export class MyAccountError extends Error {
  constructor(step, status, detail) {
    super(`My Account API ${step} failed with ${status}${detail ? `: ${detail}` : ''}`);
    this.name = 'MyAccountError';
    this.step = step;
    this.status = status;
  }
}

const readJson = async (response, step) => {
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    throw new MyAccountError(step, response.status, body?.error_description || body?.detail || body?.title);
  }
  return body;
};

/**
 * Exchanges the authorization code for a My Account API access token.
 *
 * @param {object} options
 * @param {string} options.domain - The Auth0 custom domain.
 * @param {string} options.clientId - The OIDC client.
 * @param {string} options.code - The authorization code Auth0 returned.
 * @param {string} options.verifier - The PKCE verifier saved before the redirect.
 * @returns {Promise<string>} The access token.
 */
export async function exchangeCode({ domain, clientId, code, verifier }) {
  const response = await fetch(`https://${domain}/oauth/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      grant_type: 'authorization_code',
      client_id: clientId,
      code,
      code_verifier: verifier,
      redirect_uri: `${window.location.origin}${myAccountCallback}`,
    }),
  });
  const body = await readJson(response, 'token');
  if (!body?.access_token) {
    throw new MyAccountError('token', response.status, 'no access token in the response');
  }
  return body.access_token;
}

const callMyAccount = async ({ domain, token, path, body, step }) => {
  const response = await fetch(`${myAccountBase(domain)}v1/${path}`, {
    method: 'POST',
    headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  return { response, body: await readJson(response, step) };
};

/**
 * Lists the customer's authentication methods, including a recovery code left unconfirmed by an
 * abandoned Universal Login enrollment, which the Management API omits.
 *
 * @param {object} options
 * @param {string} options.domain - The Auth0 custom domain.
 * @param {string} options.token - A My Account API access token.
 * @returns {Promise<{ id: string, type: string, confirmed?: boolean }[]>} Every method.
 */
export async function listAuthenticationMethods({ domain, token }) {
  const response = await fetch(`${myAccountBase(domain)}v1/authentication-methods`, {
    method: 'GET',
    headers: { authorization: `Bearer ${token}` },
  });
  const body = await readJson(response, 'list');
  return body?.authentication_methods ?? [];
}

/**
 * Deletes one authentication method.
 *
 * @param {object} options
 * @param {string} options.domain - The Auth0 custom domain.
 * @param {string} options.token - A My Account API access token.
 * @param {string} options.methodId - The method to delete.
 * @returns {Promise<void>}
 */
export async function deleteAuthenticationMethod({ domain, token, methodId }) {
  const response = await fetch(`${myAccountBase(domain)}v1/authentication-methods/${encodeURIComponent(methodId)}`, {
    method: 'DELETE',
    headers: { authorization: `Bearer ${token}` },
  });
  // A 204 has no body to read.
  await readJson(response, 'delete');
}

// The id arrives percent-encoded in the Location header (passkey%7Cnew). Verify rejects it with
// "Invalid authentication_method_id" unless it is decoded before being encoded again.
// The header is missing when CORS doesn't expose it, and a malformed escape can't be decoded.
const methodIdFrom = (response, body) => {
  if (body?.id) {
    return body.id;
  }
  const location = response.headers.get('location');
  if (!location) {
    throw new MyAccountError('start', response.status, 'no Location header');
  }
  try {
    return decodeURIComponent(location.split('/').pop());
  } catch (error) {
    throw new MyAccountError('start', response.status, `unreadable Location header (${error.name})`);
  }
};

const verify = ({ domain, token, methodId, body }) =>
  callMyAccount({
    domain,
    token,
    path: `authentication-methods/${encodeURIComponent(methodId)}/verify`,
    body,
    step: 'verify',
  });

/**
 * Asks Auth0 for the passkey creation options. Runs before the customer is asked to continue, so
 * the click that creates the credential has nothing to wait for first.
 *
 * @param {object} options
 * @param {string} options.domain - The Auth0 custom domain.
 * @param {string} options.token - A My Account API access token.
 * @returns {Promise<{ methodId: string, authSession: string, publicKey: object }>} What
 *   finishPasskeyEnrollment needs, with the binary fields already decoded.
 */
export async function startPasskeyEnrollment({ domain, token }) {
  const { response, body: started } = await callMyAccount({
    domain,
    token,
    path: 'authentication-methods',
    body: { type: 'passkey' },
    step: 'start',
  });
  const options = started?.authn_params_public_key;
  if (!options) {
    throw new MyAccountError('start', response.status, 'no passkey creation options');
  }
  return {
    // Passkey enrollment returns no id in the body.
    methodId: methodIdFrom(response, null),
    authSession: started.auth_session,
    publicKey: {
      ...options,
      // Auth0 asks for 'preferred'. A passkey created without user verification is single-factor
      // (NIST SP 800-63B-4), yet the Action lets a passkey sign-in stand in for two-step verification.
      authenticatorSelection: { ...options.authenticatorSelection, userVerification: 'required' },
      challenge: base64UrlToBytes(options.challenge),
      user: { ...options.user, id: base64UrlToBytes(options.user.id) },
      excludeCredentials: (options.excludeCredentials || []).map((excluded) => ({
        ...excluded,
        id: base64UrlToBytes(excluded.id),
      })),
    },
  };
}

/**
 * Creates the passkey on this device and confirms it with Auth0. Must be called straight from a
 * click: navigator.credentials.create is its first call, so no await spends the user gesture that
 * Safari requires.
 *
 * @param {object} options
 * @param {string} options.domain - The Auth0 custom domain.
 * @param {string} options.token - A My Account API access token.
 * @param {{ methodId: string, authSession: string, publicKey: object }} options.enrollment - From
 *   startPasskeyEnrollment.
 * @returns {Promise<object>} The new authentication method.
 */
export async function finishPasskeyEnrollment({ domain, token, enrollment }) {
  const credential = await navigator.credentials.create({ publicKey: enrollment.publicKey });

  const { body: method } = await verify({
    domain,
    token,
    methodId: enrollment.methodId,
    body: {
      auth_session: enrollment.authSession,
      authn_response: {
        id: credential.id,
        rawId: bytesToBase64Url(credential.rawId),
        type: credential.type,
        response: {
          attestationObject: bytesToBase64Url(credential.response.attestationObject),
          clientDataJSON: bytesToBase64Url(credential.response.clientDataJSON),
          transports: credential.response.getTransports?.(),
        },
        clientExtensionResults: credential.getClientExtensionResults?.() ?? {},
      },
    },
  });
  return method;
}

/**
 * Creates a recovery code. Auth0 shows it only in this response.
 *
 * @param {object} options
 * @param {string} options.domain - The Auth0 custom domain.
 * @param {string} options.token - A My Account API access token.
 * @returns {Promise<{ methodId: string, recoveryCode: string }>} The new method's id and the code.
 */
export async function enrollRecoveryCode({ domain, token }) {
  const { response, body: started } = await callMyAccount({
    domain,
    token,
    path: 'authentication-methods',
    body: { type: 'recovery-code' },
    step: 'start',
  });
  const methodId = methodIdFrom(response, started);
  await verify({
    domain,
    token,
    methodId,
    body: { auth_session: started.auth_session },
  });
  return { methodId, recoveryCode: started.recovery_code };
}
